import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import _ from "lodash";
import erpInventoryServices from "app/services/erp-inventory-services";
import printerServices from "app/services/printer-services";
import qzPrintServices from "app/services/qz-print-services";

const PRINTER_KEY = "haulers.inventory.printer";

// Imprime etiquetas de producto en la Zebra para los productos seleccionados.
//
// La cantidad es por producto y no global: al recibir mercadería casi nunca
// llegan las mismas unidades de cada cosa.
const PrintLabelsDialog = ({ open, onClose, products, onPrinted }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [copies, setCopies] = useState({});
    const [printers, setPrinters] = useState([]);
    const [printerId, setPrinterId] = useState(null);
    const [qzAvailable, setQzAvailable] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (!open) return;
        setRows(products || []);
        setCopies(Object.fromEntries((products || []).map((p) => [p.code, 1])));
    }, [open, products]);

    useEffect(() => {
        if (!open) return;

        Promise.all([
            qzPrintServices.isAvailable(),
            printerServices.getPrinters("?q[active_eq]=true&page=1"),
        ])
            .then(([available, resp]) => {
                setQzAvailable(available);
                const list = resp?.data || [];
                setPrinters(list);
                const last = parseInt(localStorage.getItem(PRINTER_KEY), 10);
                const hit = _.find(list, { id: last }) || list[0] || null;
                setPrinterId(hit?.id || null);
            })
            .catch(() => setPrinters([]));
    }, [open]);

    const total = useMemo(
        () => rows.reduce((sum, row) => sum + (parseInt(copies[row.code], 10) || 0), 0),
        [rows, copies]
    );

    const printer = useMemo(() => _.find(printers, { id: printerId }), [printers, printerId]);

    const print = async () => {
        const items = rows
            .map((row) => ({ code: row.code, copies: parseInt(copies[row.code], 10) || 0 }))
            .filter((item) => item.copies > 0);

        if (!items.length) return;

        try {
            setPrinting(true);
            const { zpl, count } = await erpInventoryServices.getLabels(items);

            await qzPrintServices.printZpl({
                // El campo es ip, no host: asi se llama en el modelo Printer.
                printerName: printer?.printer_name,
                host: printer?.ip,
                port: printer?.port,
                zpl,
            });

            if (printer?.id) localStorage.setItem(PRINTER_KEY, String(printer.id));
            enqueueSnackbar(`${count} etiquetas enviadas a ${printer?.name || "la impresora"}`, { variant: "success" });
            onPrinted();
            onClose();
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.error || error?.message || "No se pudo imprimir",
                { variant: "error" }
            );
        } finally {
            setPrinting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Imprimir etiquetas
                <Typography variant="body2" color="text.secondary">
                    {rows.length} {rows.length === 1 ? "producto" : "productos"} · {total} etiquetas
                </Typography>
            </DialogTitle>
            <DialogContent>
                {!qzAvailable && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        QZ Tray no está corriendo. Ábrelo antes de imprimir.
                    </Alert>
                )}

                {printers.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        No hay impresoras configuradas. Agrégalas en el mantenedor de Impresoras.
                    </Alert>
                ) : (
                    <Autocomplete
                        size="small"
                        options={printers}
                        value={printer || null}
                        getOptionLabel={(option) => option.name || ""}
                        onChange={(_event, value) => setPrinterId(value?.id || null)}
                        renderInput={(params) => <TextField {...params} label="Impresora" />}
                        sx={{ mb: 2 }}
                    />
                )}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>Etiquetas</TableCell>
                            <TableCell sx={{ width: 48 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.code}>
                                <TableCell>
                                    <Typography variant="body2">{row.code}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap
                                                sx={{ display: "block", maxWidth: 280 }}>
                                        {row.name}
                                        {!row.barcode && " · sin EAN"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={copies[row.code] ?? 1}
                                        onChange={(event) =>
                                            setCopies({ ...copies, [row.code]: event.target.value })
                                        }
                                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                                        sx={{ width: 90 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small"
                                                onClick={() => setRows(rows.filter((r) => r.code !== row.code))}>
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {rows.some((row) => !row.barcode) && (
                    <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                        <Typography variant="body2">
                            Los productos sin EAN se imprimen con el código en el barcode. Se leen igual con el
                            lector, pero no coinciden con el código de barra del fabricante.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={printing} startIcon={<PrintOutlinedIcon />}
                               disabled={!total || !printer} onClick={print}>
                    Imprimir {total > 0 ? `(${total})` : ""}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default PrintLabelsDialog;
